// Self-reported email helpers for app.tachusa.com.
//
// The marketing site (tachusa.com) captures the user's email during the
// pre-prequal flow and writes it to a cookie scoped to `.tachusa.com`, which
// IS readable here on app.tachusa.com (unlike localStorage, which does not
// cross origins). This lets the app prefill the Auth0 signup box via
// `login_hint` even when the email is missing from the login URL.
//
// Mirrors `etid.ts` exactly (cookie scoping, samesite/secure, 30-day max-age,
// localStorage mirror), with one addition: email format is validated before
// persisting so we never seed `login_hint` with garbage.

import { safeGetItem, safeSetItem } from './safeStorage';

export const SELF_REPORTED_EMAIL_STORAGE_KEY = 'selfReportedEmail';
export const SELF_REPORTED_EMAIL_COOKIE_NAME = 'tach_self_reported_email';

// 30 days — signups frequently complete in a later session than the capture.
const SELF_REPORTED_EMAIL_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

// Pragmatic format check (matches the landing-page helper). `login_hint` only
// prefills the field, so this is a sanity gate, not authoritative validation.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string | null | undefined): boolean =>
  typeof email === 'string' && EMAIL_REGEX.test(email.trim());

export const getSelfReportedEmailCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${SELF_REPORTED_EMAIL_COOKIE_NAME}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.split('=')[1] ?? '');
  return value.length > 0 ? value : null;
};

export const setSelfReportedEmailCookie = (email: string) => {
  if (typeof document === 'undefined') return;
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  // Scope to the registrable domain so the cookie is shared with the
  // marketing site (tachusa.com) — writable from app.tachusa.com.
  const isProduction = hostname.endsWith('tachusa.com');
  const domain = isProduction ? '; domain=.tachusa.com' : '';
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; secure'
      : '';
  document.cookie = `${SELF_REPORTED_EMAIL_COOKIE_NAME}=${encodeURIComponent(
    email
  )}; path=/; max-age=${SELF_REPORTED_EMAIL_COOKIE_MAX_AGE}${domain}; samesite=lax${secure}`;
};

// Remove the cross-domain cookie (mirrors setSelfReportedEmailCookie's
// scoping). Without this, logout clearing localStorage is ineffective: a later
// step falls back to the cookie and re-persists the previous user's email.
export const clearSelfReportedEmailCookie = () => {
  if (typeof document === 'undefined') return;
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isProduction = hostname.endsWith('tachusa.com');
  const domain = isProduction ? '; domain=.tachusa.com' : '';
  document.cookie = `${SELF_REPORTED_EMAIL_COOKIE_NAME}=; path=/; max-age=0${domain}; samesite=lax`;
};

// Read back a previously captured self-reported email, preferring the
// same-origin localStorage mirror and falling back to the cross-domain cookie
// (which is what survives a capture on the marketing site). Returns null when
// nothing valid is stored, so callers can treat it as a simple presence check.
export const getSelfReportedEmail = (): string | null => {
  // safeGetItem already returns null when storage is blocked, so the cookie
  // fallback below is reached without the read being able to throw.
  const stored = safeGetItem(SELF_REPORTED_EMAIL_STORAGE_KEY);
  if (isValidEmail(stored)) return stored!.trim();
  const cookie = getSelfReportedEmailCookie();
  return isValidEmail(cookie) ? cookie!.trim() : null;
};

// Persist a self-reported email to BOTH the cross-domain cookie and
// localStorage so it survives no matter which mechanism a later step reads
// from. Only valid emails are persisted.
export const persistSelfReportedEmail = (email: string) => {
  if (!isValidEmail(email)) return;
  const normalized = email.trim();
  setSelfReportedEmailCookie(normalized);
  safeSetItem(SELF_REPORTED_EMAIL_STORAGE_KEY, normalized);
};
